
import { fromJS } from 'immutable';
import * as types from '../constants';
import LocalStorageUtility from '../common/localStorageUtility/localStorageUtility';

/**
 * Sports get sorted by a drag and dropping in Backoffice tool.
 * When sorting, use this sorting condition to match the backoffice behaviour.
 * @param {Object} sportA
 * @param {Object} sportB
 * @returns {Boolean}
 */
export function sortSportsPosition(sportA, sportB) {
  if (sportA.sortingPosition < sportB.sortingPosition) {
    return -1;
  } else if (sportA.sortingPosition > sportB.sortingPosition) {
    return 1;
  }

  return 0;
}

/**
 * Filters desired sports from LiveHub component based on the current platform
 * @param {Array} sportList An array of sports
 * @returns {Array} A filtered list of sports allowed in your platform.
 */
export function getFilteredSportList(sportList) {
  let filteredSportList = sportList;

  if (process.env.REACT_APP_SITE === 'corriere') {
    filteredSportList = sportList.filter(sport => (sport.sportId !== '3'));
  }
  if (process.env.REACT_APP_SITE === 'forzaroma' || process.env.REACT_APP_SITE === 'fcinter' || process.env.REACT_APP_SITE === 'calcionapoli1926') {
    filteredSportList = sportList.filter(sport => sport.sportId === '1');
  }

  return filteredSportList;
}

/**
 * Filters competitionMatches within competitions based on their 'highlighted' param.
 * This function gets reused in two conditions:
 * 1. To get a listing of matches for the SportTable
 * 2. To get a listing of highlighted matches for the 'Da non perdere' highlighted block.
 * @param {Array} competitions
 * @param {Boolean} isHighlighted Passed manually as an argument
 * @returns The given list of competitions with a filtered and updated 'competitionMatches' array.
 */
export function filterMatches(competitions, isHighlighted) {
  for (const competition of competitions) {
    competition.competitionMatches = competition.competitionMatches.filter((match) => {
      if (!match.highlighted) {
        match.highlighted = false;
      }

      if (match.sportId && match.competitionId && match.status
        && String(match.sportId) === '18'
        && String(match.competitionId) === '7101'
        && match.status === 'COMPLETE') {
        match.highlighted = false;
      }

      return match.highlighted === isHighlighted;
    });
  }

  return competitions;
}

export function filterByLiveStatus(competitionMatches) {
  return competitionMatches.filter(game => (
    game.status === types.LIVE
    || game.trackStatus === types.ON_TRACK
    || game.status === types.INPLAY
    || game.status === types.PLAYING
    || game.status === types.INPROGRESS
  ));
}

export function liveCompetitions(filteredCompetitions) {
  const newMap = filteredCompetitions.toJS();

  Object.keys(newMap).forEach((key) => {
    if (newMap[key] && newMap[key].length) {
      newMap[key].forEach((competition) => {
        competition.competitionMatches = filterByLiveStatus(competition.competitionMatches);
      });
    }
  });

  return fromJS(newMap);
}

export function liveCompetitionsCounter(competitions, filterSportId = null) {
  if (!competitions) return 0;
  const liveMap = liveCompetitions(competitions).toJS();
  let counter = 0;
  let filteredCounter = 0;
  Object.keys(liveMap).forEach((key) => {
    if (liveMap[key] && liveMap[key].length) {
      liveMap[key].forEach((competition) => {
        competition.competitionMatches = filterByLiveStatus(competition.competitionMatches);
        counter += competition.competitionMatches.length;
        if (key === filterSportId) {
          filteredCounter += competition.competitionMatches.length;
        }
      });
    }
  });
  if (filterSportId) return filteredCounter;
  return counter;
}

export function emptyCompetitions(competitions, sportId) {
  for (const competition of competitions) {
    if (competition.competitionMatches.length) {
      if (sportId && competition.competitionId && String(sportId) === '3' && String(competition.competitionId) === '1') {
        if (competition.competitionMatches[0].competition && competition.competitionMatches[0].competition.phase) {
          competition.phase = competition.competitionMatches[0].competition.phase;
        }
        if (competition.competitionMatches[0].competition && competition.competitionMatches[0].competition.subphase) {
          competition.subphase = competition.competitionMatches[0].competition.subphase;
        }
      }

      return competitions;
    }
  }

  return [];
}

export function checkCompetitionsRows(state, live, stateName) {
  const newMap = state.get(stateName).toJS();
  const preferredMap = LocalStorageUtility.getPreferredMatch();
  Object.keys(newMap).forEach((key) => {
    if (newMap[key] && newMap[key].length) {
      newMap[key].forEach((competition) => {
        competition.competitionMatches = live ? filterByLiveStatus(competition.competitionMatches) : competition.competitionMatches;
        competition.competitionMatches = competition.competitionMatches.map((match) => {
          if (preferredMap[match.matchId] || preferredMap[match.id]) {
            match.favourite = true;
          } else {
            match.favourite = false;
          }
          return match;
        });
      });
    }
  });

  Object.keys(newMap).forEach((key) => {
    if (newMap[key] && newMap[key].length) {
      newMap[key].forEach(() => {
        newMap[key] = emptyCompetitions(newMap[key]);
      });
    }
  });

  return fromJS(newMap);
}

export function updateAvailableSports(sportFilterMap, sportFilter) {
  const newMap = sportFilterMap.toJS();
  for (const filter of newMap) {
    if (sportFilter === filter.id) {
      filter.isSelected = true;
    } else {
      filter.isSelected = false;
    }
  }
  return fromJS(newMap);
}

export function clearAvailableSports(sportFilterMap) {
  const newMap = sportFilterMap.toJS();
  for (const filter of newMap) {
    filter.isSelected = false;
  }
  return fromJS(newMap);
}
